import { LoaderStore } from "@state/stores/common";
import { SessionStorageProvider } from "@state/providers";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, computed, observable, runInAction } from "mobx";

import * as htmlToImage from "html-to-image";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { DateTime } from "luxon";
import { Media } from "@capacitor-community/media";
import { localizationService } from "@state/services";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { IBaseTip, IBetSlipRequest } from "@gp/slip";
import BetSlipApiService from "@api-services/agency/BetSlipApiService";
import { BetSlipTypes, EventType } from "@gp/models";
import { THEME_SETTINGS } from "@theme-settings";
import BetSlipStore from "./BetSlipStore";
import { PageableCollection } from "@api-types/common";
import {
	AnonReadyBet,
	ClickedTip,
	PreparedBetSlip,
	SportData,
} from "@api-types/bet-slip/SavedSlip";
import { Filter } from "@api-types/my-bets/Filter";
import { DeviceDetector, getCurrentCulture, SGUID_EMPTY_CONST } from "@utils";
import { preferLookupBettingTypeName } from "@utils/offer";
import { groupOffersByEvent } from "@utils/my-bets/helpers";
import { redirect } from "@state";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { NameProvider } from "@gp/utility";
import i18n from "@state/services/localizationService";

interface CapacitorGalleryPlugin {
	saveImage(options: { base64: string; filename: string }): Promise<void>;
}

const CapacitorGallery = registerPlugin(
	"capacitorGallery"
) as CapacitorGalleryPlugin;

export enum SavedSlipInfoType {
	POPUP = "popup",
	DRAWER = "drawer",
}

export default class SavedSlipStore {
	betSlipStore: BetSlipStore;
	loaderStore: LoaderStore;

	@observable totalRecords: number = 0;
	@observable pageNumber: number = 1;
	@observable pageSize: number = 10;

	@observable savedSlips: PageableCollection<PreparedBetSlip> | null = null;
	@observable savedSlip: PreparedBetSlip | null = null;
	@observable barcodeSlipValue: string | null = null;
	@observable barcodeSlipCode: string | null = null;

	@observable isReuseModalOpen: boolean = false;
	@observable savedSlipToReuse: PreparedBetSlip | null = null;

	@observable isDeleteModalOpen: boolean = false;
	@observable savedSlipToDelete: PreparedBetSlip | null = null;

	@observable isSavedSlipInfoPopupActive: boolean = false;
	@observable isSavedSlipInfoDrawerOpened: boolean = false;

	@observable isBarcodePopupActive: boolean = false;
	@observable isReadyBetTicketDetailsPopupActive: boolean = false;

	constructor(betSlipStore: BetSlipStore) {
		this.betSlipStore = betSlipStore;
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	public setIsSavedSlipInfoDrawerOpened(
		isSavedSlipInfoDrawerOpened: boolean
	) {
		this.isSavedSlipInfoDrawerOpened = isSavedSlipInfoDrawerOpened;
	}

	@action.bound
	public setIsSavedSlipInfoPopupActive(isSavedSlipInfoPopupActive: boolean) {
		this.isSavedSlipInfoPopupActive = isSavedSlipInfoPopupActive;
	}

	@action.bound
	public setIsBarcodePopupActive(isBarcodePopupActive: boolean) {
		this.isBarcodePopupActive = isBarcodePopupActive;
	}

	@action.bound
	public setIsReadyBetTicketDetailsPopupActive(
		isReadyBetTicketDetailsPopupActive: boolean
	) {
		this.isReadyBetTicketDetailsPopupActive =
			isReadyBetTicketDetailsPopupActive;
	}

	@action.bound
	public setSavedSlip(savedSlip: PreparedBetSlip | null) {
		this.savedSlip = savedSlip;
	}

	@action.bound
	public setBarcodeSlipValue(barcodeSlipValue: string | null) {
		this.barcodeSlipValue = barcodeSlipValue;
	}

	@action.bound
	public setBarcodeSlipCode(barcodeSlipCode: string | null) {
		this.barcodeSlipCode = barcodeSlipCode;
	}

	@action.bound
	async onInitialize(filter?: Filter) {
		try {
			this.loaderStore.suspend();

			const savedSlips = await BetSlipApiService.fetchPreparedSlips(
				filter,
				this.pageNumber,
				this.pageSize
			);

			runInAction(() => {
				if (!savedSlips) {
					this.savedSlips = this.createEmptySavedSlipsCollection();
					this.totalRecords = 0;
					return;
				}

				savedSlips.item.forEach((slip) =>
					this.groupSlipOffersByEvent(slip)
				);
				this.savedSlips = savedSlips;
				this.totalRecords = savedSlips.totalRecords;
				this.pageNumber = savedSlips.page;
			});
		} catch (error) {
			console.error(error);

			runInAction(() => {
				this.savedSlips = this.createEmptySavedSlipsCollection();
				this.totalRecords = 0;
			});

			App.state.rootStore.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.GENERIC_ERROR")
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	createEmptySavedSlipsCollection(): PageableCollection<PreparedBetSlip> {
		return {
			item: [],
			links: [],
			totalRecords: 0,
			page: this.pageNumber,
			recordsPerPage: this.pageSize,
			searchQuery: "",
			sort: "",
			embed: "",
		};
	}

	createSlipObject() {
		const slipState = this.betSlipStore.betSlipState;

		const mappedOffers = slipState.clickedTips.map((tip: ClickedTip) => {
			return {
				eventId: tip.eventId,
				id: tip.id.startsWith("multiBet-")
					? tip.id.replace("multiBet-", "")
					: tip.id,
				isBank: tip.isBank,
				isMultiOffer:
					tip.multiOffers != null && tip.multiOffers.length > 0,
				multiOffers: tip.multiOffers?.map(
					(m: IBaseTip["multiOffers"][number]) => {
						return {
							multiOfferId: m.multiOfferId.includes("-")
								? m.multiOfferId.split("-")[1]
								: m.multiOfferId,
						};
					}
				),
				value: tip.value,
			};
		});

		const slipContainer = {
			offers: mappedOffers,
			payment: slipState.totalStake,
			paymentType: slipState.paymentType,
			system:
				this.betSlipStore.betSlipType === BetSlipTypes.SYSTEM
					? slipState.selectedCombinations?.join("+")
					: undefined,
			type: this.betSlipStore.betSlipType,
		};

		return {
			maxCoefficient: slipState.slip?.maxCoefficient || 0,
			maxGain: slipState.slip?.maxGain || 0,
			totalNumberOfCombinations: slipState.combinations || 0,

			sessionId: this.createReferenceId(),
			acceptOddsChanges: slipState.acceptOddsChanges,

			...this.betSlipStore.getBetSlipRequestOrgStructureInfo(),

			betSlipsFromRequest: [slipContainer],

			bettingAccountTypeId: null,
			isBetSlipBeforeActive: false,
			isOneClickBet: false,
			preparedBetSlipId: null,
			unlockTerminalVoucherNumber: null,
		};
	}

	private getAnonReadyBet(): AnonReadyBet | null {
		const raw = SessionStorageProvider.get(
			StorageStateKeysEnum.ANON_READY_BET_KEY
		);

		if (!raw) {
			return null;
		}

		try {
			return JSON.parse(raw);
		} catch {
			return null;
		}
	}

	private setAnonReadyBet(data: AnonReadyBet): void {
		SessionStorageProvider.set(
			StorageStateKeysEnum.ANON_READY_BET_KEY,
			JSON.stringify(data)
		);
	}

	public clearAnonReadyBet(): void {
		SessionStorageProvider.remove(StorageStateKeysEnum.ANON_READY_BET_KEY);
	}

	private createSlipFingerprint(): string {
		const slipState = this.betSlipStore.betSlipState;
		const data = {
			offers: slipState.clickedTips
				.map((tip: ClickedTip) => ({
					id: tip.id.startsWith("multiBet-")
						? tip.id.replace("multiBet-", "")
						: tip.id,
					eventId: tip.eventId,
					isBank: !!tip.isBank,
					value: tip.value,
					multiOffers:
						tip.multiOffers
							?.map((m: any) => m.multiOfferId)
							.sort() ?? null,
				}))
				.sort((a, b) => a.id.localeCompare(b.id)),
			payment: slipState.totalStake,
			paymentType: slipState.paymentType,
			system:
				this.betSlipStore.betSlipType === BetSlipTypes.SYSTEM
					? (slipState.selectedCombinations?.sort().join("+") ?? null)
					: null,
			type: this.betSlipStore.betSlipType,
			acceptOddsChanges: slipState.acceptOddsChanges,
		};
		return JSON.stringify(data);
	}

	private openSavedSlipUI(savedSlipInfoType: SavedSlipInfoType): void {
		switch (savedSlipInfoType) {
			case SavedSlipInfoType.DRAWER:
				this.setIsSavedSlipInfoDrawerOpened(true);
				break;
			case SavedSlipInfoType.POPUP:
				this.setIsSavedSlipInfoPopupActive(true);
				break;
		}
	}

	@action.bound
	public async savePreparedSlip(savedSlipInfoType: SavedSlipInfoType) {
		const isAnon =
			//@ts-expect-error
			!this.betSlipStore.rootStore.userAuthStore.isLoggedIn;

		if (isAnon && THEME_SETTINGS.isReadyBetRetailEnabled) {
			const stored = this.getAnonReadyBet();

			if (
				stored &&
				stored.slipFingerprint === this.createSlipFingerprint()
			) {
				runInAction(() => {
					this.savedSlip = stored.preparedBetSlip;
					this.barcodeSlipValue = stored.betSlipNumber + "r0";
					this.barcodeSlipCode = stored.preparedBetSlip.code;
					this.openSavedSlipUI(savedSlipInfoType);
				});
				return;
			}
		}

		try {
			this.loaderStore.suspend();

			const response = await BetSlipApiService.prepare(
				this.createSlipObject() as IBetSlipRequest
			);

			if (!response?.preparedBetSlip) {
				App.state.rootStore.notificationStore.error(
					response?.errors?.[0] ??
						localizationService.t("BET_SLIP.ERRORS.GENERIC_ERROR")
				);
				return;
			}

			runInAction(() => {
				this.groupSlipOffersByEvent(response.preparedBetSlip);
				this.savedSlip = response.preparedBetSlip;
				this.barcodeSlipValue =
					response.preparedBetSlip.betSlipNumber + "r0";
				this.barcodeSlipCode = response.preparedBetSlip.code;

				if (isAnon && THEME_SETTINGS.isReadyBetRetailEnabled) {
					this.setAnonReadyBet({
						readyBetId: response.preparedBetSlip.id,
						betSlipNumber: response.preparedBetSlip.betSlipNumber,
						slipFingerprint: this.createSlipFingerprint(),
						preparedBetSlip: response.preparedBetSlip,
					});
				}

				if (!isAnon) {
					this.betSlipStore.onResetSlip();
				}

				this.openSavedSlipUI(savedSlipInfoType);
			});
		} catch (error) {
			console.error(error);

			App.state.rootStore.notificationStore.error(
				error?.data?.errors?.[0] ??
					localizationService.t("BET_SLIP.ERRORS.GENERIC_ERROR")
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async deletePreparedSlip(preparedSlipId: string | null) {
		if (!preparedSlipId) {
			return;
		}

		try {
			this.loaderStore.suspend();

			await BetSlipApiService.deletePreparedSlip(preparedSlipId);

			App.state.rootStore.notificationStore.success(
				localizationService.t("BET_SLIP.BET_SLIP_SAVED.DELETE_SUCCESS")
			);

			const isLastItemOnPage =
				this.pageNumber > 1 &&
				this.totalRecords - 1 <= (this.pageNumber - 1) * this.pageSize;

			if (isLastItemOnPage) {
				this.pageNumber -= 1;
				window.scrollTo(0, 0);
			}

			this.onInitialize(this.filter);
		} catch (error) {
			console.error(error);

			App.state.rootStore.notificationStore.error(
				localizationService.t("BET_SLIP.ERRORS.GENERIC_ERROR")
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	onPageChange(newPageNumber: number) {
		if (newPageNumber) {
			this.pageNumber = newPageNumber;
		}

		this.onInitialize(this.filter);
	}

	@computed get filter() {
		const filter: Filter = {};

		if (this.pageNumber != null) {
			filter.pageNumber = this.pageNumber;
		}

		return filter;
	}

	formattedBettingType(
		sportData: SportData | null,
		specifier?: string | null,
		bettingTypeId?: string | null
	) {
		if (!sportData) return "";

		const template = preferLookupBettingTypeName(
			this.betSlipStore.betSlipState.lookupStore,
			bettingTypeId,
			sportData.bettingTypeNameForBettingSlip
		);

		const nameProvider = new NameProvider();
		return nameProvider.getName(
			template,
			this.formattedOfferSpecifier(sportData, specifier)
		);
	}

	formattedOfferSpecifier(sportData: SportData, specifier?: string | null) {
		const parsed =
			typeof specifier === "string"
				? specifier
					? JSON.parse(specifier)
					: {}
				: (specifier ?? {});
		return {
			competitor1: sportData.teamOneName,
			competitor2: sportData.teamTwoName,
			...parsed,
		};
	}

	formattedEventName(sportData: SportData | null): string {
		if (!sportData) {
			return "";
		}

		if (sportData.teamOneName != null && sportData.teamTwoName != null) {
			return `${sportData.teamOneName} - ${sportData.teamTwoName}`;
		}

		return sportData.eventName ?? sportData.tournamentName ?? "";
	}

	formattedBettingTypeTip(
		offer:
			| {
					sportData?: SportData | null;
					specifier?: string | null;
					bettingTypeTip?: string | null;
					teamId?: string | null;
					teamName?: string | null;
					playerId?: string | null;
					playerFirstName?: string | null;
					playerLastName?: string | null;
			  }
			| null
			| undefined
	): string {
		if (!offer) {
			return "";
		}

		const { sportData, specifier, bettingTypeTip } = offer;

		if (!bettingTypeTip) {
			return "";
		}

		const isOutrightSignal =
			(sportData?.eventType != null &&
				sportData.eventType !== EventType.NORMAL) ||
			bettingTypeTip.startsWith("pre:outcometext:");

		if (isOutrightSignal) {
			if (offer.playerId != null) {
				return `${offer.playerLastName}, ${offer.playerFirstName}`;
			}
			if (offer.teamName != null) {
				return offer.teamName;
			}
		}

		if (!sportData) {
			return bettingTypeTip.startsWith("pre:") ? "" : bettingTypeTip;
		}

		const nameProvider = new NameProvider();

		const resolved = nameProvider.getName(
			bettingTypeTip,
			this.formattedOfferSpecifier(sportData, specifier)
		);

		return resolved.startsWith("pre:") ? "" : resolved;
	}

	//#region Download Barcode

	async createAlbum(albumName: string) {
		const albumResults = await Media.getAlbums();
		const existingAlbum = albumResults.albums.find(
			(a) => a.name === albumName
		);

		if (existingAlbum) {
			return existingAlbum.identifier;
		}

		await Media.createAlbum({ name: albumName });
		const updated = await Media.getAlbums();
		return (
			updated.albums.find((a) => a.name === albumName)?.identifier ?? ""
		);
	}

	async saveTicketToGallery(ticketRef: HTMLDivElement | null) {
		if (!ticketRef) return;

		try {
			this.loaderStore.suspend();

			const dataUrl = await htmlToImage.toPng(ticketRef, {
				pixelRatio: 2,
			});

			if (Capacitor.getPlatform() == "android") {
				const base64Data = dataUrl.split(",")[1];

				await CapacitorGallery.saveImage({
					base64: base64Data,
					filename: `ticket-${DateTime.now()}.png`,
				});
			} else {
				const albumId = await this.createAlbum("Tickets");

				await Media.savePhoto({
					path: dataUrl,
					albumIdentifier: albumId,
					fileName: `ticket-${Date.now()}`,
				});
			}
		} catch (err) {
			console.error("Failed to save ticket", err);
		} finally {
			this.loaderStore.resume();
		}
	}

	async handleDownloadTicket(
		ticketRef: React.MutableRefObject<HTMLDivElement | null>
	) {
		await new Promise((resolve) => setTimeout(resolve, 100)); //delay for temp render

		if (!ticketRef.current) return;

		if (isCapacitorPlatform()) {
			await this.saveTicketToGallery(ticketRef.current);
			return;
		}

		const dataUrl = await htmlToImage.toPng(ticketRef.current, {
			pixelRatio: 2,
		});

		const link = document.createElement("a");
		link.download = "ticket.png";
		link.href = dataUrl;
		link.click();
	}

	//#endregion

	@action.bound
	public openReuseModal(savedSlip: PreparedBetSlip) {
		this.savedSlipToReuse = savedSlip;
		this.isReuseModalOpen = true;
	}

	@action.bound
	public closeReuseModal() {
		this.isReuseModalOpen = false;
		this.savedSlipToReuse = null;
	}

	@action.bound
	public reuseSlip() {
		if (!this.savedSlipToReuse) return;

		const mappedSlip = this.mapPreparedSlipForReuse(this.savedSlipToReuse);

		this.betSlipStore.useBetSlip(mappedSlip, undefined, {
			trackAsReuse: true,
		});

		if (DeviceDetector.isDesktopTheme) {
			redirect(`/${getCurrentCulture()}/${DefaultPath.PREMATCH}`);
		} else {
			redirect(`/${getCurrentCulture()}/bet-slip`);
		}

		this.closeReuseModal();
	}

	@action.bound
	public openDeleteModal(savedSlip: PreparedBetSlip) {
		this.savedSlipToDelete = savedSlip;
		this.isDeleteModalOpen = true;
	}

	@action.bound
	public closeDeleteModal() {
		this.isDeleteModalOpen = false;
		this.savedSlipToDelete = null;
	}

	@action.bound
	public confirmDeleteSlip() {
		if (!this.savedSlipToDelete) return;

		const id = this.savedSlipToDelete.id;
		this.closeDeleteModal();
		this.deletePreparedSlip(id);
	}

	@action.bound
	public mapPreparedSlipForReuse(savedSlip: PreparedBetSlip) {
		return {
			...savedSlip,
			betSlipOffers: savedSlip.preparedBetSlipOffers,
			betSlipType: {
				name: savedSlip.system
					? BetSlipTypes.SYSTEM
					: BetSlipTypes.COMBINATION,
			},
		};
	}

	groupSlipOffersByEvent(slip: PreparedBetSlip) {
		slip.preparedBetSlipOffers = groupOffersByEvent(
			slip.preparedBetSlipOffers
		).flat();
	}

	getNumberOfBets = (slip: PreparedBetSlip): number =>
		slip.totalNumberOfCombinations || 1;

	getFormattedSystemString = (slip: PreparedBetSlip, t: i18n.TFunction) => {
		const interpolation = { escapeValue: false };
		const offers = slip.preparedBetSlipOffers || [];

		const eventCount = new Set(offers.map((o) => o.eventId)).size;

		const bankCount = new Set(
			offers.filter((o) => o.isBank).map((o) => o.eventId)
		).size;

		const system = slip.system;

		if (system) {
			let systemString = `${system} / ${eventCount - bankCount} = ${this.getNumberOfBets(slip)}`;

			if (bankCount > 0) {
				systemString = `B x ${bankCount}, ${systemString}`;
			}

			return t("COMMON.INFO_SYSTEM", {
				interpolation,
				0: `(${systemString})`,
			});
		}

		const totalCombinations = this.getNumberOfBets(slip);

		if (totalCombinations > 1) {
			return t("COMMON.INFO_COMBINATION_MULTI", {
				interpolation,
				0: totalCombinations,
			});
		}

		return t("COMMON.INFO_COMBINATION", {
			interpolation,
			0: totalCombinations,
		});
	};

	@action.bound
	createReferenceId(): string {
		const shopId =
			//@ts-expect-error
			this.betSlipStore.rootStore.userAuthStore.shopId;
		const userId =
			//@ts-expect-error
			this.betSlipStore.rootStore.userAuthStore.user?.userNumericalId;

		const timestamp = String(Date.now());

		if (!userId) {
			return SGUID_EMPTY_CONST + "-" + shopId + "-" + timestamp;
		} else {
			return userId + "-" + shopId + "-" + timestamp;
		}
	}

	@action.bound
	public onDispose() {
		this.setIsSavedSlipInfoDrawerOpened(false);
		this.setIsSavedSlipInfoPopupActive(false);
		this.setIsBarcodePopupActive(false);
		this.setIsReadyBetTicketDetailsPopupActive(false);
		this.barcodeSlipValue = null;
		this.barcodeSlipCode = null;
		this.savedSlips = null;
		this.savedSlip = null;
		this.totalRecords = 0;
		this.pageNumber = 1;
		this.pageSize = 10;
		this.closeReuseModal();
		this.closeDeleteModal();
	}
}
